import React, {useContext, useEffect, useMemo, useState} from "react";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import OrderDashboardFilters from "../dashboard/OrderDashboard/Layout/OrderDashboardFilters.jsx";
import {FilterContext} from "../../components/filters/DashboardFilterContextProvider.jsx";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {ErpCalculationApi, OtherSystemType, ProjectApi, ProjectIntegrationsApi} from "../../api/authority/generated/index";
import {useReportingError} from "../../api/utilities/useReportingError.js";
import toast from "react-hot-toast";
import EChart from "./ReportingEChart.jsx";
import {useRenderCurrencyValue, useRenderNumber} from "../../api/utilities/useValueRenderers.js";
import {useTranslation} from "react-i18next";
import ReportingInfoTooltip from "../../components/ReportingInfoTooltip.jsx";
import {marketingDomainSpendReconciliation} from "../../utils/marketingDomainAuditPresentation.js";
import {
    ChannelMixColors,
    formatAxisTooltip,
    formatChartNumber,
    formatNumberItemTooltip,
    KpiCard,
    moneyAmount,
    moneyToMajor,
    Panel,
    ReportingPageDescription,
} from "./ReportingLab.jsx";

const otherSystemTypes = [
    OtherSystemType.Mailing,
    OtherSystemType.Affiliate,
    OtherSystemType.Other,
];

function integrationHealth(integration) {
    if ((integration.errors ?? []).length > 0) {
        return "error";
    }

    if (integration.authorizationSuccessful === false) {
        return "unauthorized";
    }

    return "ok";
}

function statusLabel(t, status) {
    switch (status) {
        case 'ok':
            return t('reporting_lab.data_quality.status.ok');
        case 'unauthorized':
            return t('reporting_lab.data_quality.status.unauthorized');
        case 'error':
            return t('reporting_lab.data_quality.status.error');
        default:
            return status;
    }
}

function integrationDisplayName(integration) {
    return integration.name || "-";
}

function typeTranslationKey(type) {
    return `other-sources.types.${type}`;
}

export default function ReportingDataQualityLab({project}) {
    const {t} = useTranslation();
    const {since, until, centresAllowListed, domainsAllowListed} = useContext(FilterContext);
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const renderNumber = useRenderNumber();
    const renderCurrencyValue = useRenderCurrencyValue();
    const [integrations, setIntegrations] = useState([]);
    const [domains, setDomains] = useState([]);
    const [otherSources, setOtherSources] = useState([]);
    const [orderAudit, setOrderAudit] = useState(null);
    const [marketingAudit, setMarketingAudit] = useState(null);
    const [projectDomainAudit, setProjectDomainAudit] = useState(null);
    const [auditIntegrationTypes, setAuditIntegrationTypes] = useState([]);
    const [auditDecisions, setAuditDecisions] = useState([]);
    const [loading, setLoading] = useState(true);
    const {et, retentionExpired} = useReportingError(project.id, setLoading);

    const sinceString = since.format("YYYY-MM-DD");
    const untilString = until.format("YYYY-MM-DD");
    const centreFilterKey = (centresAllowListed ?? []).join("|");
    const domainFilterKey = (domainsAllowListed ?? []).join("|");

    const erpFilter = useMemo(() => ({
        since: sinceString,
        until: untilString,
        selectedCentreIds: centresAllowListed,
        selectedDomainIds: domainsAllowListed,
    }), [sinceString, untilString, centreFilterKey, domainFilterKey]);

    useEffect(() => {
        if (!project?.id) {
            return;
        }

        let active = true;
        setLoading(true);
        setIntegrations([]);
        setDomains([]);
        setOtherSources([]);
        setOrderAudit(null);
        setMarketingAudit(null);
        setProjectDomainAudit(null);

        const projectApi = new ProjectApi(getAuthorityConfiguration());
        const integrationsApi = new ProjectIntegrationsApi(getAuthorityConfiguration());
        const erpApi = new ErpCalculationApi(getAuthorityConfiguration());

        Promise.all([
            integrationsApi.listProjectIntegrations({projectId: project.id}),
            projectApi.listProjectDomains({projectId: project.id}),
            Promise.all(otherSystemTypes.map((type) => projectApi.otherSystemSources({
                projectId: project.id,
                otherSystemType: type,
            }).then((sources) => (sources ?? []).map((source) => ({type, source}))))),
            erpApi.orderDomainCentreAudit({projectId: project.id, erpFilter}),
            erpApi.marketingDomainAudit({projectId: project.id, erpFilter}),
            erpApi.projectDomainAudit({
                projectId: project.id,
                projectDomainAuditInput: {
                    since: sinceString,
                    until: untilString,
                    integrationTypes: auditIntegrationTypes,
                    decisions: auditDecisions,
                },
            }),
        ])
            .then(([nextIntegrations, nextDomains, sourceGroups, nextOrderAudit, nextMarketingAudit, nextProjectDomainAudit]) => {
                if (!active) {
                    return;
                }

                const selectedCentreIds = new Set(centresAllowListed ?? []);
                const selectedDomainIds = new Set(domainsAllowListed ?? []);
                const filteredSources = sourceGroups.flat()
                    .filter(({source}) => {
                        const centreId = source.otherSystemSource?.centreId;
                        const domainId = source.otherSystemSource?.domainId;
                        const centreAllowed = selectedCentreIds.size === 0 || selectedCentreIds.has(centreId);
                        const domainAllowed = selectedDomainIds.size === 0 || selectedDomainIds.has(domainId);

                        return centreAllowed && domainAllowed;
                    });

                setIntegrations(nextIntegrations ?? []);
                setDomains(nextDomains ?? []);
                setOtherSources(filteredSources);
                setOrderAudit(nextOrderAudit);
                setMarketingAudit(nextMarketingAudit);
                setProjectDomainAudit(nextProjectDomainAudit);
                setLoading(false);
            })
            .catch((error) => {
                if (!active) {
                    return;
                }

                et(error).then((message) => { if (message) toast.error(message); });
            });

        return () => {
            active = false;
        };
    }, [project?.id, erpFilter, auditIntegrationTypes, auditDecisions]);

    const enabledDomains = domains.filter((domain) => domain.projectDomain?.isEnabled);
    const activeDomains = domains.filter((domain) => (domain.projectDomainStats?.adCount ?? 0) > 0 || (domain.projectDomainStats?.campaignCount ?? 0) > 0);
    const billedNoiseDomains = domains.filter((domain) => !domain.projectDomain?.isEnabled && ((domain.projectDomainStats?.adCount ?? 0) > 0 || (domain.projectDomainStats?.campaignCount ?? 0) > 0));
    const domainWithoutCentre = domains.filter((domain) => (domain.centres ?? []).length === 0);
    const sourcesWithoutDomain = otherSources.filter(({source}) => !source.projectDomain);
    const sourcesWithoutCentre = otherSources.filter(({source}) => !source.projectCentre);
    const domainsById = new Map(domains.map((domain) => [domain.projectDomain?.id, domain]));
    const sourcesWithDomainCentreMismatch = otherSources.filter(({source}) => {
        const domainId = source.otherSystemSource?.domainId;
        const centreId = source.otherSystemSource?.centreId;
        const domain = domainsById.get(domainId);

        return !!domainId
            && !!centreId
            && !!domain
            && !(domain.centres ?? []).some((centre) => centre.id === centreId);
    });
    const integrationsWithProblems = integrations.filter((integration) => integrationHealth(integration) !== "ok");
    const orderDomainCentrePairs = orderAudit?.pairs ?? [];
    const orderAuditRiskCount = Number(orderAudit?.ordersWithoutDomain ?? 0) + Number(orderAudit?.ordersOnDisabledDomains ?? 0);
    const marketingDomainRows = marketingAudit?.rows ?? [];
    const marketingSpendReconciliation = marketingDomainSpendReconciliation(marketingAudit);
    const marketingAuditRiskCount = Number(marketingAudit?.campaignsWithoutDomain ?? 0)
        + Number(marketingAudit?.adsWithoutDomain ?? 0)
        + Number(marketingAudit?.campaignsOnDisabledDomains ?? 0)
        + Number(marketingAudit?.adsOnDisabledDomains ?? 0);
    const marketingAuditRiskSpend = moneyAmount(marketingAudit?.spendWithoutDomain) + moneyAmount(marketingAudit?.spendOnDisabledDomains);
    const projectDomainAuditRows = projectDomainAudit?.rows ?? [];

    const statusRows = useMemo(() => {
        const statuses = new Map();

        integrations.forEach((integration) => {
            const status = integrationHealth(integration);
            statuses.set(status, (statuses.get(status) ?? 0) + 1);
        });

        return ["ok", "unauthorized", "error"].map((status) => ({
            name: statusLabel(t, status),
            value: statuses.get(status) ?? 0,
        })).filter((row) => row.value > 0);
    }, [integrations, t]);

    const domainRows = domains
        .map((domain) => ({
            name: domain.projectDomain?.hostName ?? "-",
            campaigns: domain.projectDomainStats?.campaignCount ?? 0,
            ads: domain.projectDomainStats?.adCount ?? 0,
            enabled: domain.projectDomain?.isEnabled === true,
            builtin: domain.projectDomain?.isBuiltin === true,
            centreCount: domain.centres?.length ?? 0,
        }))
        .sort((a, b) => (b.campaigns + b.ads) - (a.campaigns + a.ads));

    const marketingSpendRows = [...marketingDomainRows]
        .filter((row) => moneyAmount(row.spend) > 0 || row.campaignCount > 0 || row.adCount > 0)
        .sort((a, b) => moneyAmount(b.spend) - moneyAmount(a.spend));

    const sourceRows = otherSources
        .map(({type, source}) => ({
            type,
            name: source.otherSystemSource?.name ?? "-",
            domain: source.projectDomain?.hostName ?? t("reporting_lab.data_quality.missing_domain", {defaultValue: "Bez domény"}),
            centre: source.projectCentre?.name ?? t("reporting_lab.data_quality.missing_centre", {defaultValue: "Bez střediska"}),
            hasDomain: !!source.projectDomain,
            hasCentre: !!source.projectCentre,
        }))
        .sort((a, b) => Number(a.hasDomain && a.hasCentre) - Number(b.hasDomain && b.hasCentre));

    const chartTextStyle = {fontSize: 12, color: "#64748b"};
    const chartLegendStyle = {
        textStyle: {fontSize: 14, color: "#64748b"},
        itemWidth: 16,
        itemHeight: 10,
    };

    const statusOption = {
        color: ["#16a34a", "#f59e0b", "#ef4444"],
        textStyle: chartTextStyle,
        tooltip: {
            trigger: "item",
            formatter: formatNumberItemTooltip,
        },
        legend: {bottom: 0, left: "center", width: "92%", ...chartLegendStyle},
        series: [
            {
                type: "pie",
                radius: ["44%", "66%"],
                center: ["50%", "44%"],
                avoidLabelOverlap: true,
                label: {
                    show: true,
                    formatter: (params) => `${params.name}: ${formatChartNumber(params.value)}`,
                    ...chartTextStyle,
                },
                labelLine: {show: true, length: 12, length2: 8},
                data: statusRows,
            },
        ],
    };

    const domainOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 18, bottom: 42, containLabel: true},
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => formatAxisTooltip(params, formatChartNumber),
        },
        legend: {bottom: 0, left: "center", ...chartLegendStyle},
        xAxis: {
            type: "value",
            axisLabel: {fontSize: 12, formatter: formatChartNumber},
        },
        yAxis: {
            type: "category",
            data: [...domainRows].reverse().slice(0, 10).map((row) => row.name),
            axisLabel: {fontSize: 12, width: 220, overflow: "truncate"},
        },
        series: [
            {
                name: t("reporting_lab.data_quality.campaigns", {defaultValue: "Kampaně"}),
                type: "bar",
                stack: "total",
                data: [...domainRows].reverse().slice(0, 10).map((row) => row.campaigns),
                barMaxWidth: 22,
            },
            {
                name: t("reporting_lab.data_quality.ads", {defaultValue: "Reklamy"}),
                type: "bar",
                stack: "total",
                data: [...domainRows].reverse().slice(0, 10).map((row) => row.ads),
                barMaxWidth: 22,
            },
        ],
    };

    const marketingSpendOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 18, bottom: 42, containLabel: true},
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => formatAxisTooltip(params, (value) => renderCurrencyValue({
                amount: Math.round(Number(value ?? 0) * 100),
                currency: marketingSpendRows[0]?.spend?.currency,
            }, true)),
        },
        xAxis: {
            type: "value",
            axisLabel: {
                fontSize: 12,
                formatter: (value) => formatChartNumber(value),
            },
        },
        yAxis: {
            type: "category",
            data: [...marketingSpendRows].reverse().slice(0, 10).map((row) => row.domainName),
            axisLabel: {fontSize: 12, width: 220, overflow: "truncate"},
        },
        series: [
            {
                name: t("reporting_lab.data_quality.marketing_spend", {defaultValue: "Marketing spend"}),
                type: "bar",
                data: [...marketingSpendRows].reverse().slice(0, 10).map((row) => moneyToMajor(row.spend)),
                barMaxWidth: 22,
            },
        ],
    };

    return (
        <WideSecureLayout periodReport retentionExpired={retentionExpired}
            title={t("reporting_lab.pages.data_quality", {defaultValue: "Datová kvalita"})}
            actions={<OrderDashboardFilters project={project} showModeSwitch={false}/>}
         intro={<ReportingPageDescription
                    title={t("reporting_lab.descriptions.data_quality.title", {defaultValue: "K čemu slouží datová kvalita"})}
                >
                    {t("reporting_lab.descriptions.data_quality.text", {defaultValue: "Interní kontrola mapování domén, středisek, integrací a ručně evidovaných nákladů. Pomáhá najít chyby, které mohou zkreslit reporting. Marketingový doménový audit pracuje s doménou reklamy a střediskový filtr se na něj neuplatní."})}
                </ReportingPageDescription>}>
            <div className="space-y-4">

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
                        <KpiCard loading={loading}
                            label={t("reporting_lab.data_quality.enabled_domains", {defaultValue: "Aktivní domény"})}
                            value={`${renderNumber(enabledDomains.length)} / ${renderNumber(domains.length)}`}
                            helper={t("reporting_lab.data_quality.enabled_domains_help", {defaultValue: "Zapnuté z celkového počtu domén"})}
                        />
                        <KpiCard loading={loading}
                            label={t("reporting_lab.data_quality.integration_problems", {defaultValue: "Problémy integrací"})}
                            value={renderNumber(integrationsWithProblems.length)}
                            helper={t("reporting_lab.data_quality.integration_problems_help", {defaultValue: "Chyby nebo neautorizované integrace"})}
                            tone={integrationsWithProblems.length > 0 ? "bad" : "good"}
                        />
                        <KpiCard loading={loading}
                            label={t("reporting_lab.data_quality.unmapped_sources", {defaultValue: "Nemapped zdroje"})}
                            value={renderNumber(new Set([...sourcesWithoutDomain, ...sourcesWithoutCentre, ...sourcesWithDomainCentreMismatch].map(({source}) => source.otherSystemSource?.id)).size)}
                            helper={t("reporting_lab.data_quality.unmapped_sources_help", {defaultValue: "Ostatní náklady bez domény, střediska nebo s neplatnou vazbou doména/středisko"})}
                            tone={sourcesWithoutDomain.length + sourcesWithoutCentre.length + sourcesWithDomainCentreMismatch.length > 0 ? "warn" : "good"}
                        />
                        <KpiCard loading={loading}
                            label={t("reporting_lab.data_quality.order_domain_risks", {defaultValue: "Rizika objednávek"})}
                            value={renderNumber(orderAuditRiskCount)}
                            helper={t("reporting_lab.data_quality.order_domain_risks_help", {defaultValue: "Objednávky bez domény nebo na vypnuté doméně"})}
                            tone={orderAuditRiskCount > 0 ? "warn" : "good"}
                        />
                        <KpiCard loading={loading}
                            label={t("reporting_lab.data_quality.marketing_domain_risks", {defaultValue: "Rizika marketingu"})}
                            value={renderNumber(marketingAuditRiskCount)}
                            helper={marketingAuditRiskSpend > 0
                                ? renderCurrencyValue({amount: marketingAuditRiskSpend, currency: marketingAudit?.spendWithoutDomain?.currency ?? marketingAudit?.spendOnDisabledDomains?.currency}, true)
                                : t("reporting_lab.data_quality.marketing_domain_risks_help", {defaultValue: "Kampaně/reklamy bez domény nebo na vypnuté doméně"})}
                            tone={marketingAuditRiskCount > 0 || marketingAuditRiskSpend > 0 ? "warn" : "good"}
                        />
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                        <Panel loading={loading} title={t("reporting_lab.data_quality.integration_status", {defaultValue: "Stav integrací"})}>
                            <EChart option={statusOption} height={360}/>
                        </Panel>
                        <Panel loading={loading} title={t("reporting_lab.data_quality.domain_marketing_coverage", {defaultValue: "Marketingové vazby podle domén"})}>
                            <EChart option={domainOption} height={360}/>
                        </Panel>
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                        <Panel loading={loading} title={t("reporting_lab.data_quality.marketing_spend_by_domain", {defaultValue: "Marketing spend podle domén"})}>
                            <EChart option={marketingSpendOption} height={360}/>
                            {marketingSpendReconciliation && <div className="mt-4 grid grid-cols-1 gap-3 border-t border-slate-200 pt-4 text-sm dark:border-slate-700 md:grid-cols-2">
                                <div>
                                    <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                                        <span>{t("reporting_lab.data_quality.connected_marketing_spend", {defaultValue: "Spend připojených reklamních systémů"})}</span>
                                        <ReportingInfoTooltip>
                                            {t("reporting_lab.data_quality.connected_marketing_spend_help", {defaultValue: "Zahrnuje pouze propojené reklamní systémy. Pro každou kampaň a den má přednost hodnota kampaně, včetně nuly; pokud chybí, použije se hodnota reklamy. Ručně evidované náklady OtherMedia nejsou zahrnuty. Tato částka nepotvrzuje úplnost všech zdrojů."})}
                                        </ReportingInfoTooltip>
                                    </div>
                                    <div className="mt-1 font-semibold text-slate-900 dark:text-slate-100">
                                        {renderCurrencyValue(marketingSpendReconciliation.canonicalConnectedSpend, true)}
                                    </div>
                                </div>
                                <div>
                                    <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                                        <span>{t("reporting_lab.data_quality.connected_spend_residual", {defaultValue: "Rozdíl vůči součtu domén"})}</span>
                                        <ReportingInfoTooltip>
                                            {t("reporting_lab.data_quality.connected_spend_residual_help", {defaultValue: "Jde o podepsaný rozdíl: spend připojených systémů minus součet existujících efektivních hodnot přiřazených domén. Rozdíl není přiřazen ke konkrétní doméně a neurčuje jeho příčinu."})}
                                        </ReportingInfoTooltip>
                                    </div>
                                    <div className="mt-1 font-semibold text-slate-900 dark:text-slate-100">
                                        {renderCurrencyValue(marketingSpendReconciliation.coverageResidual, true)}
                                    </div>
                                </div>
                            </div>}
                        </Panel>
                        <Panel loading={loading} title={t("reporting_lab.data_quality.marketing_domain_risks", {defaultValue: "Rizika marketingu"})}>
                            <div className="divide-y divide-slate-100 dark:divide-slate-700">
                                <RiskRow
                                    label={t("reporting_lab.data_quality.campaigns_without_domain", {defaultValue: "Kampaně bez domény"})}
                                    value={Number(marketingAudit?.campaignsWithoutDomain ?? 0)}
                                    detail={t("reporting_lab.data_quality.campaigns_without_domain_help", {defaultValue: "Spend z těchto kampaní nejde spolehlivě filtrovat podle projektové domény"})}
                                    renderNumber={renderNumber}
                                />
                                <RiskRow
                                    label={t("reporting_lab.data_quality.ads_without_domain", {defaultValue: "Reklamy bez domény"})}
                                    value={Number(marketingAudit?.adsWithoutDomain ?? 0)}
                                    detail={t("reporting_lab.data_quality.ads_without_domain_help", {defaultValue: "Ad-level spend bez domény může zkreslit doménové reporty"})}
                                    renderNumber={renderNumber}
                                />
                                <RiskRow
                                    label={t("reporting_lab.data_quality.marketing_on_disabled_domains", {defaultValue: "Marketing na vypnutých doménách"})}
                                    value={Number(marketingAudit?.campaignsOnDisabledDomains ?? 0) + Number(marketingAudit?.adsOnDisabledDomains ?? 0)}
                                    detail={t("reporting_lab.data_quality.marketing_on_disabled_domains_help", {defaultValue: "Kampaně nebo reklamy navázané na domény, které nejsou aktivní pro projekt"})}
                                    renderNumber={renderNumber}
                                />
                                <RiskRow
                                    label={t("reporting_lab.data_quality.spend_without_domain", {defaultValue: "Spend bez domény"})}
                                    value={moneyAmount(marketingAudit?.spendWithoutDomain)}
                                    detail={renderCurrencyValue(marketingAudit?.spendWithoutDomain, true)}
                                    renderNumber={(value) => renderCurrencyValue({amount: value, currency: marketingAudit?.spendWithoutDomain?.currency}, true)}
                                />
                                <RiskRow
                                    label={t("reporting_lab.data_quality.spend_on_disabled_domains", {defaultValue: "Spend na vypnutých doménách"})}
                                    value={moneyAmount(marketingAudit?.spendOnDisabledDomains)}
                                    detail={renderCurrencyValue(marketingAudit?.spendOnDisabledDomains, true)}
                                    renderNumber={(value) => renderCurrencyValue({amount: value, currency: marketingAudit?.spendOnDisabledDomains?.currency}, true)}
                                />
                            </div>
                        </Panel>
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                        <Panel loading={loading} title={t("reporting_lab.data_quality.mapping_risks", {defaultValue: "Rizika mapování"})}>
                            <div className="divide-y divide-slate-100 dark:divide-slate-700">
                                <RiskRow
                                    label={t("reporting_lab.data_quality.disabled_domains_with_stats", {defaultValue: "Vypnuté domény s daty"})}
                                    value={billedNoiseDomains.length}
                                    detail={billedNoiseDomains.map((domain) => domain.projectDomain?.hostName).filter(Boolean).join(", ")}
                                    renderNumber={renderNumber}
                                />
                                <RiskRow
                                    label={t("reporting_lab.data_quality.domains_without_centre", {defaultValue: "Domény bez střediska"})}
                                    value={domainWithoutCentre.length}
                                    detail={domainWithoutCentre.map((domain) => domain.projectDomain?.hostName).filter(Boolean).join(", ")}
                                    renderNumber={renderNumber}
                                />
                                <RiskRow
                                    label={t("reporting_lab.data_quality.sources_without_domain", {defaultValue: "Ostatní zdroje bez domény"})}
                                    value={sourcesWithoutDomain.length}
                                    detail={sourcesWithoutDomain.map(({source}) => source.otherSystemSource?.name).filter(Boolean).join(", ")}
                                    renderNumber={renderNumber}
                                />
                                <RiskRow
                                    label={t("reporting_lab.data_quality.sources_without_centre", {defaultValue: "Ostatní zdroje bez střediska"})}
                                    value={sourcesWithoutCentre.length}
                                    detail={sourcesWithoutCentre.map(({source}) => source.otherSystemSource?.name).filter(Boolean).join(", ")}
                                    renderNumber={renderNumber}
                                />
                                <RiskRow
                                    label={t("reporting_lab.data_quality.source_domain_centre_mismatch", {defaultValue: "Nesoulad doména/středisko u ostatních zdrojů"})}
                                    value={sourcesWithDomainCentreMismatch.length}
                                    detail={sourcesWithDomainCentreMismatch.map(({source}) => `${source.otherSystemSource?.name ?? "-"} (${source.projectDomain?.hostName ?? "-"} / ${source.projectCentre?.name ?? "-"})`).join(", ")}
                                    renderNumber={renderNumber}
                                />
                                <RiskRow
                                    label={t("reporting_lab.data_quality.orders_without_domain", {defaultValue: "Objednávky bez domény"})}
                                    value={Number(orderAudit?.ordersWithoutDomain ?? 0)}
                                    detail={t("reporting_lab.data_quality.orders_without_domain_help", {defaultValue: "Objednávky, které nejdou spolehlivě filtrovat podle domény"})}
                                    renderNumber={renderNumber}
                                />
                                <RiskRow
                                    label={t("reporting_lab.data_quality.orders_on_disabled_domains", {defaultValue: "Objednávky na vypnutých doménách"})}
                                    value={Number(orderAudit?.ordersOnDisabledDomains ?? 0)}
                                    detail={t("reporting_lab.data_quality.orders_on_disabled_domains_help", {defaultValue: "Objednávky na doménách, které nejsou aktivní pro projekt"})}
                                    renderNumber={renderNumber}
                                />
                            </div>
                        </Panel>

                        <Panel loading={loading} title={t("reporting_lab.data_quality.integrations", {defaultValue: "Integrace"})}>
                            <div className="overflow-x-auto">
                                <table className="table-auto w-full text-sm">
                                    <thead className="text-xs uppercase text-slate-500 bg-slate-50 dark:bg-slate-800/60">
                                    <tr>
                                        <th className="px-4 py-3 text-left">{t("reporting_lab.data_quality.integration", {defaultValue: "Integrace"})}</th>
                                        <th className="px-4 py-3 text-left">{t("reporting_lab.data_quality.status_label", {defaultValue: "Stav"})}</th>
                                        <th className="px-4 py-3 text-left">{t("reporting_lab.data_quality.last_regular_update", {defaultValue: "Poslední update"})}</th>
                                        <th className="px-4 py-3 text-right">{t("reporting_lab.data_quality.errors", {defaultValue: "Chyby"})}</th>
                                    </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                    {integrations.map((integration) => {
                                        const status = integrationHealth(integration);

                                        return (
                                            <tr key={integration.id ?? integrationDisplayName(integration)}>
                                                <td className="px-4 py-3 font-medium text-slate-800 dark:text-slate-100">{integrationDisplayName(integration)}</td>
                                                <td className="px-4 py-3">
                                                    <StatusBadge status={status} label={statusLabel(t, status)}/>
                                                </td>
                                                <td className="px-4 py-3 text-slate-500">{integration.lastRegularUpdate ?? "-"}</td>
                                                <td className="px-4 py-3 text-right text-slate-500">{renderNumber((integration.errors ?? []).length)}</td>
                                            </tr>
                                        );
                                    })}
                                    </tbody>
                                </table>
                            </div>
                        </Panel>
                    </div>

                    <Panel loading={loading} title={t("reporting_lab.data_quality.order_domain_centre_pairs", {defaultValue: "Objednávky podle domény a střediska"})}>
                        <div className="overflow-x-auto">
                            <table className="table-auto w-full text-sm">
                                <thead className="text-xs uppercase text-slate-500 bg-slate-50 dark:bg-slate-800/60">
                                <tr>
                                    <th className="px-4 py-3 text-left">{t("reporting_lab.data_quality.domain", {defaultValue: "Doména"})}</th>
                                    <th className="px-4 py-3 text-left">{t("reporting_lab.data_quality.centre", {defaultValue: "Středisko"})}</th>
                                    <th className="px-4 py-3 text-left">{t("reporting_lab.data_quality.status_label", {defaultValue: "Stav"})}</th>
                                    <th className="px-4 py-3 text-right">{t("reporting_lab.orders.orders", {defaultValue: "Objednávky"})}</th>
                                </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                {orderDomainCentrePairs.map((row) => {
                                    const status = row.domainId && row.domainEnabled !== false ? "ok" : "error";

                                    return <tr key={`${row.domainId ?? "missing"}-${row.centreId}`}>
                                        <td className="px-4 py-3 font-medium text-slate-800 dark:text-slate-100">{row.domainName}</td>
                                        <td className="px-4 py-3 text-slate-500">{row.centreSlug && row.centreSlug !== "def" ? `[${row.centreSlug}] ${row.centreName}` : row.centreName}</td>
                                        <td className="px-4 py-3">
                                            <StatusBadge
                                                status={status}
                                                label={status === "ok"
                                                    ? t("reporting_lab.data_quality.mapped", {defaultValue: "Spárováno"})
                                                    : t("reporting_lab.data_quality.order_domain_risk", {defaultValue: "Riziko domény"})}
                                            />
                                        </td>
                                        <td className="px-4 py-3 text-right text-slate-500">{renderNumber(row.orderCount ?? 0)}</td>
                                    </tr>;
                                })}
                                </tbody>
                            </table>
                        </div>
                    </Panel>

                    <Panel loading={loading} title={t("reporting_lab.data_quality.marketing_domain_pairs", {defaultValue: "Marketing podle domény"})}>
                        <div className="overflow-x-auto">
                            <table className="table-auto w-full text-sm">
                                <thead className="text-xs uppercase text-slate-500 bg-slate-50 dark:bg-slate-800/60">
                                <tr>
                                    <th className="px-4 py-3 text-left">{t("reporting_lab.data_quality.domain", {defaultValue: "Doména"})}</th>
                                    <th className="px-4 py-3 text-left">{t("reporting_lab.data_quality.status_label", {defaultValue: "Stav"})}</th>
                                    <th className="px-4 py-3 text-right">{t("reporting_lab.data_quality.campaigns", {defaultValue: "Kampaně"})}</th>
                                    <th className="px-4 py-3 text-right">{t("reporting_lab.data_quality.ads", {defaultValue: "Reklamy"})}</th>
                                    <th className="px-4 py-3 text-right">{t("reporting_lab.data_quality.spend", {defaultValue: "Spend"})}</th>
                                </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                {marketingSpendRows.map((row) => {
                                    const status = row.domainId && row.domainEnabled !== false ? "ok" : "error";

                                    return <tr key={row.domainId ?? "missing-domain"}>
                                        <td className="px-4 py-3 font-medium text-slate-800 dark:text-slate-100">{row.domainName}</td>
                                        <td className="px-4 py-3">
                                            <StatusBadge
                                                status={status}
                                                label={status === "ok"
                                                    ? t("reporting_lab.data_quality.mapped", {defaultValue: "Spárováno"})
                                                    : t("reporting_lab.data_quality.marketing_domain_risk", {defaultValue: "Riziko domény"})}
                                            />
                                        </td>
                                        <td className="px-4 py-3 text-right text-slate-500">{renderNumber(row.campaignCount ?? 0)}</td>
                                        <td className="px-4 py-3 text-right text-slate-500">{renderNumber(row.adCount ?? 0)}</td>
                                        <td className="px-4 py-3 text-right text-slate-500">{renderCurrencyValue(row.spend, true)}</td>
                                    </tr>;
                                })}
                                </tbody>
                            </table>
                        </div>
                    </Panel>

                    <Panel loading={loading} title={t("reporting_lab.data_quality.project_domain_audit", {defaultValue: "Audit rozhodnutí domén"})}>
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
                                <AuditKpi label={t("reporting_lab.data_quality.domain_audit_fallbacks", {defaultValue: "Fallbacky"})} value={projectDomainAudit?.fallbackCount ?? 0} renderNumber={renderNumber}/>
                                <AuditKpi label={t("reporting_lab.data_quality.domain_audit_created", {defaultValue: "Nově vytvořené"})} value={projectDomainAudit?.createdCount ?? 0} renderNumber={renderNumber}/>
                                <AuditKpi label={t("reporting_lab.data_quality.domain_audit_unresolved", {defaultValue: "Bez výsledku"})} value={projectDomainAudit?.unresolvedCount ?? 0} renderNumber={renderNumber}/>
                                <AuditKpi label={t("reporting_lab.data_quality.domain_audit_known_hosts", {defaultValue: "Známé hosty"})} value={projectDomainAudit?.knownHostCount ?? 0} renderNumber={renderNumber}/>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <AuditMultiSelect
                                    label={t("reporting_lab.data_quality.domain_audit_integration", {defaultValue: "Integrace"})}
                                    value={auditIntegrationTypes}
                                    options={["google-ads", "meta-ads", "meta", "sklik", "microsoft-advertising", "pinterest", "shoptet", "bs-shop", "page-event"]}
                                    onChange={setAuditIntegrationTypes}
                                />
                                <AuditMultiSelect
                                    label={t("reporting_lab.data_quality.domain_audit_decision", {defaultValue: "Rozhodnutí"})}
                                    value={auditDecisions}
                                    options={["created", "existing", "matched-order", "fallback-largest", "unresolved"]}
                                    onChange={setAuditDecisions}
                                />
                            </div>
                            <div className="overflow-x-auto">
                                <table className="table-auto w-full text-sm">
                                    <thead className="text-xs uppercase text-slate-500 bg-slate-50 dark:bg-slate-800/60">
                                    <tr>
                                        <th className="px-3 py-3 text-left">{t("reporting_lab.data_quality.domain", {defaultValue: "Host"})}</th>
                                        <th className="px-3 py-3 text-left">{t("reporting_lab.data_quality.integration", {defaultValue: "Integrace"})}</th>
                                        <th className="px-3 py-3 text-left">{t("reporting_lab.data_quality.domain_audit_evidence", {defaultValue: "Zdroj URL / entita"})}</th>
                                        <th className="px-3 py-3 text-left">{t("reporting_lab.data_quality.domain_audit_decision", {defaultValue: "Rozhodnutí"})}</th>
                                        <th className="px-3 py-3 text-right">{t("reporting_lab.data_quality.domain_audit_occurrences", {defaultValue: "Výskytů"})}</th>
                                        <th className="px-3 py-3 text-right">{t("reporting_lab.data_quality.domain_audit_last_seen", {defaultValue: "Naposledy"})}</th>
                                    </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                    {projectDomainAuditRows.map((row, index) => (
                                        <tr key={`${row.source}-${row.remoteEntityId ?? "none"}-${row.hostName ?? "none"}-${index}`}>
                                            <td className="px-3 py-3 font-medium text-slate-800 dark:text-slate-100">{row.hostName ?? t("reporting_lab.data_quality.missing_domain", {defaultValue: "Bez hostu"})}</td>
                                            <td className="px-3 py-3 text-slate-500">{row.source}{row.flow ? ` · ${row.flow}` : ""}</td>
                                            <td className="px-3 py-3 text-slate-500 max-w-md">
                                                <div className="truncate" title={row.sourceUrl ?? undefined}>{row.sourceUrl ?? row.urlField ?? "-"}</div>
                                                <div className="text-xs truncate" title={row.reason ?? undefined}>{[row.entityType, row.remoteEntityId, row.urlField, row.reason].filter(Boolean).join(" · ")}</div>
                                                {(row.advertisingCategory || row.advertisingDestinationStatus || row.advertisingClassificationReason) &&
                                                    <div
                                                        className="mt-1 text-xs font-medium text-violet-600 dark:text-violet-300 truncate"
                                                        title={row.advertisingClassificationReason ?? undefined}
                                                    >
                                                        {[row.advertisingCategory, row.advertisingDestinationStatus, row.advertisingClassificationReason].filter(Boolean).join(" · ")}
                                                    </div>}
                                            </td>
                                            <td className="px-3 py-3 text-slate-500">{row.decision}{row.selectedDomainHostName ? ` → ${row.selectedDomainHostName}` : ""}</td>
                                            <td className="px-3 py-3 text-right text-slate-500 whitespace-nowrap">{renderNumber(row.occurrenceCount ?? 0)}</td>
                                            <td className="px-3 py-3 text-right text-slate-500 whitespace-nowrap">{formatAuditTimestamp(row.lastSeenAt)}</td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </Panel>

                    <Panel loading={loading} title={t("reporting_lab.data_quality.other_sources_mapping", {defaultValue: "Mapování ostatních nákladů"})}>
                        <div className="overflow-x-auto">
                            <table className="table-auto w-full text-sm">
                                <thead className="text-xs uppercase text-slate-500 bg-slate-50 dark:bg-slate-800/60">
                                <tr>
                                    <th className="px-4 py-3 text-left">{t("reporting_lab.data_quality.source", {defaultValue: "Zdroj"})}</th>
                                    <th className="px-4 py-3 text-left">{t("reporting_lab.data_quality.type", {defaultValue: "Typ"})}</th>
                                    <th className="px-4 py-3 text-left">{t("reporting_lab.data_quality.domain", {defaultValue: "Doména"})}</th>
                                    <th className="px-4 py-3 text-left">{t("reporting_lab.data_quality.centre", {defaultValue: "Středisko"})}</th>
                                    <th className="px-4 py-3 text-left">{t("reporting_lab.data_quality.mapping", {defaultValue: "Mapování"})}</th>
                                </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                {sourceRows.map((row, index) => (
                                    <tr key={`${row.type}-${row.name}-${index}`}>
                                        <td className="px-4 py-3 font-medium text-slate-800 dark:text-slate-100">{row.name}</td>
                                        <td className="px-4 py-3 text-slate-500">{t(typeTranslationKey(row.type), {defaultValue: row.type})}</td>
                                        <td className="px-4 py-3 text-slate-500">{row.domain}</td>
                                        <td className="px-4 py-3 text-slate-500">{row.centre}</td>
                                        <td className="px-4 py-3">
                                            <StatusBadge
                                                status={row.hasDomain && row.hasCentre ? "ok" : "error"}
                                                label={row.hasDomain && row.hasCentre
                                                    ? t("reporting_lab.data_quality.mapped", {defaultValue: "Spárováno"})
                                                    : t("reporting_lab.data_quality.not_mapped", {defaultValue: "Nedopárováno"})}
                                            />
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </Panel>
                </div>
        </WideSecureLayout>
    );
}

function AuditKpi({label, value, renderNumber}) {
    return (
        <div className="app-surface rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800/50">
            <div className="text-xs text-slate-500">{label}</div>
            <div className="text-lg font-bold text-slate-800 dark:text-slate-100">{renderNumber(value)}</div>
        </div>
    );
}

function AuditMultiSelect({label, value, options, onChange}) {
    return (
        <label className="block text-sm text-slate-600 dark:text-slate-300">
            <span className="mb-1 block">{label}</span>
            <select
                className="w-full rounded-md border-slate-300 text-sm dark:border-slate-600 dark:bg-slate-800"
                multiple
                value={value}
                onChange={(event) => onChange(Array.from(event.target.selectedOptions, (option) => option.value))}
            >
                {options.map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
        </label>
    );
}

function formatAuditTimestamp(value) {
    if (value instanceof Date) {
        return value.toLocaleString();
    }

    return value ?? "-";
}

function RiskRow({label, value, detail, renderNumber}) {
    const hasRisk = value > 0;

    return (
        <div className="flex items-start justify-between gap-4 py-4">
            <div className="min-w-0">
                <div className="font-medium text-slate-800 dark:text-slate-100">{label}</div>
                <div className="text-sm text-slate-500 truncate max-w-2xl">{detail || "-"}</div>
            </div>
            <div className={`text-lg font-bold ${hasRisk ? "text-amber-600" : "text-emerald-600"}`}>
                {renderNumber(value)}
            </div>
        </div>
    );
}

function StatusBadge({status, label}) {
    const className = {
        ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
        unauthorized: "bg-amber-50 text-amber-700 border-amber-200",
        error: "bg-rose-50 text-datixo-negative border-rose-200",
    }[status] ?? "bg-slate-50 text-slate-600 border-slate-200";

    return (
        <span className={`inline-flex items-center rounded-md border px-2 py-1 text-xs font-semibold ${className}`}>
            {label}
        </span>
    );
}
